/**
 * The minimum bases (M4S-5777 X12): the one home of every base threshold a crosstab reads.
 *
 *  - `MIN_LETTER_BASE` is the fixed floor for significance letters (PRD Appendix A rule 10: "Under
 *    30 answerers, no letters"). A column a table's test reads with fewer answerers than this,
 *    counted raw, gets no letters and no other column cites it. It is NOT a setting: until X12 the
 *    floor was the person's suppression threshold, which the rail lets go down to 10, so lowering
 *    the grey-out also lowered the floor. The floor is now the larger of the two
 *    (`letterFloor`), so a threshold above 30 still keeps its columns unlettered as before.
 *  - `DEFAULT_SUPPRESSION` is where the grey-out threshold starts (`DEFAULT_SETTINGS`).
 *  - `DEFAULT_LOW_BASE_WARN` is where the low-base warning starts.
 *
 * `one-rule.guard.test.ts` fails on a `?? 30` / `= 30` beside a base or suppression name anywhere
 * else in the crosstab. The iOOH deck keeps its own floors in `slide-editor/iooh-base.ts` on
 * purpose (its spec, docs/pdlc/iooh-real-data-deck.md), and its footnotes say so.
 *
 * Pure.
 */

/** Fewer answerers than this in a column, counted raw, and the column gets no letters. */
export const MIN_LETTER_BASE = 30;

/** The grey-out (suppression) threshold a crosstab starts on. */
export const DEFAULT_SUPPRESSION = 30;

/** The low-base warning a crosstab starts on. */
export const DEFAULT_LOW_BASE_WARN = 100;

/**
 * The letters floor for a table: never below `MIN_LETTER_BASE`, and the suppression threshold when
 * that is higher (a column greyed out for its size is never lettered either). A missing or bad
 * threshold reads as the default.
 */
export function letterFloor(suppressionThreshold: number | null | undefined): number {
    const t =
        typeof suppressionThreshold === 'number' && Number.isFinite(suppressionThreshold)
            ? suppressionThreshold
            : DEFAULT_SUPPRESSION;
    return Math.max(MIN_LETTER_BASE, t);
}
